import { useId, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTrackedAddress } from '../hooks/useTrackedAddress';
import { useWalletHistory } from '../hooks/useWalletHistory';
import { TransferList } from '../components/TransferList';
import { Skeleton } from '../components/Skeleton';
import { Button } from '../components/Button';
import { Segmented } from '../components/Segmented';
import type { ConfirmationState, TransferDirection } from '../lib/types';
import styles from './HistoryPage.module.css';

type DirectionFilter = TransferDirection | 'all';
type ConfirmationFilter = ConfirmationState | 'all';

const parseDirection = (raw: string | null): DirectionFilter =>
  raw === 'in' || raw === 'out' ? raw : 'all';
const parseConfirmation = (raw: string | null): ConfirmationFilter =>
  raw === 'confirmed' || raw === 'pending' ? raw : 'all';

/**
 * The filters live in the address (?direction=, ?status=, ?q=), so they
 * survive opening a transfer and coming back, a reload, or a shared link.
 * Each change replaces the history entry rather than adding one.
 */
function useHistoryFilters() {
  const [params, setParams] = useSearchParams();
  const set = (changes: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value === '' || value === 'all') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  return {
    direction: parseDirection(params.get('direction')),
    confirmation: parseConfirmation(params.get('status')),
    search: params.get('q') ?? '',
    setDirection: (value: DirectionFilter) => set({ direction: value }),
    setConfirmation: (value: ConfirmationFilter) => set({ status: value }),
    setSearch: (value: string) => set({ q: value }),
    clear: () => set({ direction: 'all', status: 'all', q: '' }),
  };
}

export function HistoryPage() {
  const { tracked } = useTrackedAddress();
  const historyQuery = useWalletHistory(tracked.address, 25);

  const {
    direction,
    confirmation,
    search,
    setDirection,
    setConfirmation,
    setSearch,
    clear,
  } = useHistoryFilters();
  const searchId = useId();
  const searchRef = useRef<HTMLInputElement>(null);

  const allTransfers = useMemo(
    () => historyQuery.data?.pages.flatMap((page) => page.transfers) ?? [],
    [historyQuery.data],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allTransfers.filter((transfer) => {
      if (direction !== 'all' && transfer.direction !== direction) return false;
      if (confirmation !== 'all' && transfer.confirmationState !== confirmation)
        return false;
      if (query && !transfer.txid.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [allTransfers, direction, confirmation, search]);

  const filtersActive =
    direction !== 'all' || confirmation !== 'all' || search.trim() !== '';
  const clearFilters = clear;

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Transaction history</h1>
      <div className={styles.subheadingRow}>
        <p className={styles.subheading}>{tracked.label}</p>
        <a
          className={styles.feedLink}
          href={`/api/wallet-feed?address=${encodeURIComponent(tracked.address)}`}
          type="application/rss+xml"
        >
          RSS feed for this wallet
        </a>
      </div>

      <form className={styles.filters} role="search" onSubmit={(e) => e.preventDefault()}>
        <div className={styles.chips}>
          <Segmented
            label="Direction"
            value={direction}
            onChange={setDirection}
            options={[
              { value: 'all', label: 'All' },
              { value: 'in', label: 'In' },
              { value: 'out', label: 'Out' },
            ]}
          />
          <Segmented
            label="Confirmation"
            value={confirmation}
            onChange={setConfirmation}
            options={[
              { value: 'all', label: 'Any status' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
        </div>
        <div className={styles.search}>
          <label htmlFor={searchId} className="visually-hidden">
            Search transaction ID
          </label>
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14Zm5-2 4 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            id={searchId}
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by transaction ID"
          />
          {search && (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => {
                setSearch('');
                // The button disappears once the field is empty; keep focus
                // in the field rather than dropping it to the page.
                searchRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      </form>

      <section className={styles.listCard} aria-label="Transfers">
        {historyQuery.isLoading ? (
          <Skeleton width="100%" height="16rem" />
        ) : historyQuery.isError ? (
          <p className={styles.error} role="alert">
            Transaction history is temporarily unavailable.
          </p>
        ) : (
          <>
            <TransferList
              transfers={filtered}
              groupByDay
              emptyState={
                filtersActive ? (
                  <div className={styles.empty}>
                    {/* Filters only search what's been loaded so far. */}
                    {historyQuery.hasNextPage ? (
                      <>
                        <p>
                          No transfers found in the {allTransfers.length} loaded so far.
                        </p>
                        <div className={styles.emptyActions}>
                          <Button
                            onClick={() => historyQuery.fetchNextPage()}
                            disabled={historyQuery.isFetchingNextPage}
                          >
                            {historyQuery.isFetchingNextPage
                              ? 'Loading…'
                              : 'Search older transfers'}
                          </Button>
                          <button
                            type="button"
                            className={styles.textButton}
                            onClick={clearFilters}
                          >
                            Clear filters
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p>No transfers found for these filters.</p>
                        <Button onClick={clearFilters}>Clear filters</Button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={styles.empty}>
                    <p>No transfers found for this address yet.</p>
                    <p className={styles.emptyHint}>
                      New activity shows up here automatically. To follow a different
                      wallet, <Link to="/settings">change the tracked address</Link>.
                    </p>
                  </div>
                )
              }
            />
            {historyQuery.hasNextPage && filtered.length > 0 && (
              <Button
                className={styles.loadMore}
                onClick={() => historyQuery.fetchNextPage()}
                disabled={historyQuery.isFetchingNextPage}
              >
                {historyQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
